import * as md from 'marked'
import * as a from '@mitranim/js/all.mjs'
import * as dr from '@mitranim/js/dom_reg.mjs'
import * as uc from './util-coll.mjs'
import * as ut from './util-text.mjs'
import * as um from './util-misc.mjs'
import * as uv from './util-view.mjs'
import {A} from './util-view.mjs'
import * as c from './conf.mjs'

function E(...src) {return uv.ren.elemHtml(...src)}

export class MdOpt extends a.Emp {
  constructor(mdOpt, miscOpt) {
    super()
    this.initMdOpt(mdOpt)
    this.initMiscOpt(miscOpt)
    this.initComponents()
  }

  get emptyLinks() {return this.renderer.emptyLinks}

  initMdOpt(src) {
    a.patch(this, md.defaults)
    a.patch(this, src)
  }

  buk() {return this.ctx?.pageDat.buk}

  initMiscOpt(src) {
    const {ctx, galleryId} = a.laxStruct(src)
    a.priv(this, `ctx`, ctx)

    this.galleryId = (
      a.optStr(galleryId) ||
      uv.makeGalleryId(ctx?.seq.nextId() || ut.rndId())
    )
  }

  get Tokenizer() {return MdTok}
  get Lexer() {return MdLex}
  get Renderer() {return MdRen}
  get Parser() {return MdPar}

  // Must initialize and assign in this EXACT order:
  // tokenizer → lexer → renderer → parser.
  initComponents() {
    this.tokenizer = new this.Tokenizer(this)
    this.lexer = new this.Lexer(this)
    this.renderer = new this.Renderer(this)
    this.parser = new this.Parser(this)
  }

  run(src) {
    src = a.laxStr(src)
    return src && this.parser.parse(this.lexer.lex(src))
  }
}

class MdTok extends md.Tokenizer {}

class MdLex extends md.Lexer {
  omitTokens(val) {
    a.reqReg(val)
    return a.filter(this.tokens, ({text}) => !val.test(text))
  }
}

class MdPar extends md.Parser {
  parse(...src) {return a.trim(super.parse(...src))}
}

/*
Our Markdown sources should be pre-sanitized on the server, removing ALL HTML
tags. On the client, there's only a few gotchas to take care of.

Discrepancies with "standard" Markdown:

  * Single newline is treated as linebreak, not as space. Like on GitHub and
    Discord. This is more consistent with what the user sees in an input field,
    and makes the result less surprising.
*/
class MdRen extends md.Renderer {
  emptyLinks = undefined

  // Implemented as getter for consistency with view components.
  get ctx() {return this.options?.ctx}
  buk() {return this.options?.buk()}
  galleryId() {return this.options?.galleryId}
  text(src) {return brLines(src)}

  link(href, title, text) {
    if (!text && href) {
      (this.emptyLinks ??= []).push(href)
      return ``
    }

    href = ut.strTrim(href)
    if (/\bjavascript:|\bvbscript:|\bdata:/.test(href)) {
      return text
    }
    return this.anchor(href, title, text)
  }

  iframe(props) {
    if (c.DEV_DISABLE_IFRAMES) return ``
    return E(`iframe`, props).outerHTML
  }

  // Partially redundant with `VimeoPlayer..init` and `VimeoEmbed`, TODO dedup if possible.
  video(src) {
    if (!src || c.DEV_DISABLE_IFRAMES) return ``

    return a.laxStr(this.iframe({
      src,
      class: `iframe-video`,
      title: `Video player`,
      frameborder: 0,
      allow: `accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`,
      allowfullscreen: true,
    }))
  }

  anchor(href, title, text, props) {
    if (!href && !text) return ``

    props = A.with(props).href(href).title(title)
    if (/^\w+:/.test(href)) props.tarblan()
    else props.is(`a-link`)

    const out = E(`a`, props)
    out.innerHTML = text
    return out.outerHTML
  }

  anchorExt(href, title, text, props) {
    if (!href && !text) return ``
    const out = E(`a`, A.with(props).href(href).title(title).tarblan())
    out.innerHTML = text
    return out.outerHTML
  }

  /*
  TODO avoid conversion of elements to `.outerHTML` and then back to elements.
  Consider if Marked's renderer can be integrated with Prax to avoid collecting
  the output as a string, and collect it as a list of stringables / HTMLables
  instead.
  */
  image(href, title, text) {
    if (!(href = a.renderLax(href))) return ``

    const GalleryThumbImg = dr.Reg.main.tagToCls.get(`gallery-thumb-img`)

    return E(`div`, A.cls(`row-sta-cen mah-24 over-hidden round-x2`),
      E(
        new GalleryThumbImg(this.ctx).init({src: href}, this.galleryId()),
        A
          .src(href)
          .title(title)
          .alt(text)
          .cls(`bg-skeleton round-x2 w-full`),
      )
    ).outerHTML
  }

  imageLink(src, href) {
    if (!src) return ``
    return this.anchor(href, ``, super.image(src, ``, ``))
  }

  codespan(src) {return super.codespan(a.reqStr(src))}

  // Allows callers to access individual paragraphs, rather than all at once.
  paragraph(...src) {
    const out = a.reqStr(super.paragraph(...src))
    if (this.options.paragraphs) (this.paragraphs ??= []).push(out)
    return out
  }
}

export function mdToHtml(src) {return new MdOpt().run(src)}

export const XLN_OPEN_NEW_TAB = `Open in new browser tab`

/*
Discrepancies with "standard" Markdown:

  * See `MdRen` discrepancies.
  * Various extensions:
    * [x] Support for auto-detect and embed of YT links.
    * [x] Support for auto-detect and embed of other video links.
    * [x] Support for auto-detect and embed of image links.
    * [x] Support for auto-detect and embed of giphy links.
    * [ ] Support for hidden image and video embeds.
      * Part of the content, but not editable.
    * [x] Mentions via `@`.
    * [ ] Channel links via `#`.
    * [ ] Display only one embed.
    * [x] Wrap image with link.
    * [ ] Wrap image with original link.
*/
export class MdRenMsg extends MdRen {
  suffixes = undefined
  suffixesInit() {return this.suffixes ??= []}

  /*
  Supports additional features such as mentions and tags.
  Should be kept in sync with the equivalent parser in `cs-core`.

  Known issue: running multiple replacements on the same text may cause
  overlapping transformations. TODO better approach.
  */
  text(src) {
    src = a.reqStr(super.text(src))
    src = a.reqStr(this.replaceMentions(src))
    src = a.reqStr(this.replaceTags(src))
    src = a.reqStr(this.replaceSearchText(src))
    return src
  }

  /*
  Regex must be kept in sync with `cs-core` rules for Markdown parsing.

  Supports two formats: `<@mention>` (obsolete) and `@mention`. The obsolete
  format is still used in some older msgs. The `&lt;` and `&gt;` fallback is
  necessary for `<@mention>` due to premature escaping by Marked.
  */
  replaceMentions(src) {
    const replace = (src, cap0, cap1, cap2) => (
      a.optStr(this.mention(cap0 || cap1 || cap2)) ??
      a.reqStr(this.mentionFallback(src))
    )
    return src.replace(/(?:<@)([\w-]+)(?:>)|(?:&lt;@)([\w-]+)(?:&gt;)|@([\w-]+)/g, replace)
  }

  /*
  TODO:

    * Support more entity types.

    * Consider a richer tooltip on hover and/or on click. Consider something
      like Discord profile preview tooltips.
  */
  mention(key) {
    a.reqStr(key)

    const per = this.buk()?.person(key)
    if (!per) return undefined

    return E(
      `a`,
      A
        .is(`a-link`)
        .href(per.sitePath())
        .tarblan()
        .cls(`decorate-link aria-label-plain`)
        .tooltip(XLN_OPEN_NEW_TAB),
      `@`,
      per.getName(),
    ).outerHTML
  }

  /*
  TODO: render something more useful. For example, we could provide a hover
  tooltip with a hint.
  */
  mentionFallback(src) {return this.codespan(src)}

  /*
  Regex must be kept in sync with `cs-core` rules for Markdown parsing. It uses
  a trick. It matches garbage we don't want to replace, without capturing it,
  then captures stuff we actually want to replace. We must exclude the
  following:

    * HTML escape codes starting with `&#`. They appear here because Marked
      escapes text before invoking our methods.

    * Digit-only expressions like `#1`, `#23`, and so on. Hapless users
      sometimes use them for numbering, which is a relatively common
      convention outside of Markdown.

  Trick source:

    http://www.rexegg.com/regex-best-trick.html
  */
  replaceTags(src) {
    const replace = (src, key) => (
      a.isNil(key) ? src : (
        a.optStr(this.tag(key)) ??
        a.reqStr(this.tagFallback(src))
      )
    )
    return src.replace(/&#|#\d+(?![\w-])|#([\w-]+)/g, replace)
  }

  /*
  TODO:

    * Consider a richer tooltip on hover and/or on click.

    * Support more entity types.

      * In our system, slugs are not unique between different entity types. The
        same slug may correspond simultaneously to a category and to a person.
        Proper support for multiple entity types requires us to render multiple
        previews, allowing the viewer to select between different entities. For
        example, an onclick tooltip may render a category preview AND a person
        preview, with different links.
  */
  tag(key) {
    a.reqStr(key)

    const cat = this.buk()?.category(key)
    if (!cat) return undefined

    return E(
      `a`,
      A
        .is(`a-link`)
        .href(cat.sitePathDefault())
        .tarblan()
        .cls(`decorate-link aria-label-plain`)
        .tooltip(XLN_OPEN_NEW_TAB),
      `#` + key,
    ).outerHTML
  }

  /*
  TODO: render something more useful. For example, we could provide a hover
  tooltip with a hint.
  */
  tagFallback(src) {return this.codespan(src)}

  /*
  Might be good enough for now, but ideally, instead of using URL query, this
  should have access to the `BukPage` from which messages are obtained, and use
  `page.input.searchText`.
  */
  searchText() {return this.ctx?.url.query.get(c.SEARCH_TEXT_KEY)}

  searchReg = undefined
  getSearchReg() {
    const search = this.searchText()
    return this.searchReg ??= a.vac(search) && new RegExp(
      /*
      The initial part of the regexp allows to avoid capturing HTML tags
      generated by replacement of mentions and tags. This is an unfortunate
      workaround for running multiple replacements on the same text, which is
      something we should avoid. Uses a trick to avoid capturing stuff we don't
      want:

        http://www.rexegg.com/regex-best-trick.html
      */
      /(?:<[^>]*>)|(?:<[/][^>]*>)|/.source + `(` + a.regEsc(search) + `)`,
      `ig`,
    )
  }

  replaceSearchText(src) {
    a.reqStr(src)

    const reg = this.getSearchReg()
    if (!reg) return src

    const replace = (src, match) => match ? this.highlight(match) : src
    return src.replace(reg, replace)
  }

  searchHighlight(src) {return this.highlight(src)}

  highlight(src) {
    return E(`span`, A.cls(`bg-accent-light`), src).outerHTML
  }

  /*
  TODO:

    * Link wrapped in `<` and `>` -> don't embed.
  */
  link(href, title, text) {
    if (!href) return text
    const url = ut.url(href)
    this.embed(url)
    return super.link(href, title, text === href ? url.preview() : text)
  }

  embed(url) {
    a.reqInst(url, a.Url)
    if (this.appended(this.video(ut.youtubeUrlToEmbedUrl(url)))) return
    if (this.appended(this.video(ut.vimeoUrlToEmbedUrl(url)))) return
    if (this.appended(this.image(ut.giphyUrlToEmbedUrl(url)))) return
    if (this.appended(this.image(ut.onlyImageUrl(url)))) return
  }

  appended(val) {return !!a.vac(val) && this.suffixesInit().push(val)}
  sufLen() {return a.len(this.suffixes)}
  suffix() {return a.joinOptLax(this.suffixes, ``)}
}

export class MdOptMsg extends MdOpt {
  get Renderer() {return MdRenMsg}

  /*
  Show only embeds in message, no text, if the number of non-newline tokens on
  the first level matches the number of embeds. Valid only after `super.run`.
  */
  isEmbedOnly() {
    const tokens = this.lexer.omitTokens(/\n+/)
    const len = this.renderer.sufLen()
    return len > 0 && a.len(tokens) === len
  }

  run(src) {
    src = super.run(src)
    if (this.isEmbedOnly()) src = undefined
    return a.strLax(src, this.renderer.suffix())
  }
}

/*
TODO better name. Intended for rendering Markdown in constrained environments
such as inside of anchors, where some inner elements are forbidden by HTML
rules. For example, `a` is not allowed to contain `a`, `p` is not allowed to
contain `p`, and so on.

TODO disable heading IDs here.
*/
export class MdRenRestricted extends MdRen {
  // Duplicated from `MdRenText`, TODO dedup.
  link(href, title, text) {return a.laxStr(href || title || text)}
}

// TODO better name.
export class MdOptRestricted extends MdOpt {
  get Renderer() {return MdRenRestricted}
}

/*
Note: we override most methods because our rules are very different from
`md.TextRenderer`.
*/
class MdRenText extends md.TextRenderer {
  code(text)              {return a.laxStr(text) + `\n`}
  blockquote(text)        {return a.laxStr(text) + `\n`}
  heading(text)           {return a.laxStr(text) + `\n`}
  hr()                    {return `` + `\n`}
  html()                  {return ``}
  list(text)              {return a.laxStr(text) + `\n`}
  listitem(text)          {return `* ` + a.laxStr(text) + `\n`}
  checkbox(checked)       {return checked ? `☑︎` : `☐`}
  paragraph(text)         {return a.laxStr(text) + `\n`}
  table()                 {return ``}
  tablerow()              {return ``}
  tablecell()             {return ``}
  strong(text)            {return a.laxStr(text)}
  em(text)                {return a.laxStr(text)}
  codespan(text)          {return a.laxStr(text)}
  br()                    {return `\n`}
  del()                   {return ``}
  link(href, title, text) {return a.laxStr(href || title || text)}
  image()                 {return ``}
  text(src)               {return a.laxStr(src)}
}

class MdOptHtmlText extends MdOpt {get Renderer() {return MdRenText}}

export class MdOptAi extends MdOpt {get Renderer() {return MdRenAi}}

export class MdRenAi extends MdRen {
  // Render all links as tarblans to avoid losing current convo due to navigation.
  link = this.anchorExt
}

/*
The output of this function should be rendered as HTML, because it may contain
special HTML escape sequences. Additionally, line breaks should be respected,
either by using CSS properties such as `white-space: pre-wrap`, or by splitting
the string and converting each `\n` into a `<br>` element.
*/
export function mdToHtmlText(src) {return new MdOptHtmlText().run(src)}

export function mdToTextEll(src, len) {
  len = a.reqNat(len)
  src = a.trunc(src, Math.max(len * 2, 64))
  return a.ell(mdToText(src), len)
}

// Note: many entities come with `.preview` generated on the server.
// Prefer server-generated preview when possible.
export function mdPreviewShort(src) {
  return mdToTextEll(src, c.PREVIEW_LENGTH_SHORT)
}

// Note: many entities come with `.preview` generated on the server.
// Prefer server-generated preview when possible.
export function mdPreviewMid(src) {
  return mdToTextEll(src, c.PREVIEW_LENGTH_MID)
}

/*
TODO remove. Marked assumes that its output is always rendered as HTML, even
when using a "text renderer" that elides HTML tags. We should always render
output as HTML instead of unescaping. It's simpler and more robust.
*/
class MdOptText extends MdOptHtmlText {
  run(src) {return htmlUnescape(super.run(src))}
}

// TODO remove. Needs additional handling of linebreaks; see `mdToHtmlText`.
export function mdToText(src) {return new MdOptText().run(src)}

/*
Can be used to revert unnecessary escaping performed by Marked.
TODO remove.
*/
export function htmlUnescape(str) {
  return a.laxStr(str).replace(/&#(\d+);|&#x([0-9A-Fa-f]+);|&(\w+);/ig, mdUnescapeMatch)
}

function mdUnescapeMatch(str, dec, hex, exact) {
  if (dec) return String.fromCharCode(dec)
  if (hex) return String.fromCharCode(Number.parseInt(hex, 16))

  if (exact) {
    exact = exact.toLowerCase()

    if (exact === `quot`) return `"`
    if (exact === `lt`)   return `<`
    if (exact === `gt`)   return `>`
    if (exact === `amp`)  return `&`
    if (exact === `apos`) return `'`
    if (exact === `nbsp`) return `\u00a0`
  }

  return str
}

function brLines(src) {
  return a.laxStr(src).replace(/(\r\n|\r|\n)/g, `<br>`)
}

/*
Tool for editing Marked AST.

The input must be an array of Marked "tokens" (AST nodes) generated by a Marked
Lexer. The function is called for each token, and returns either nil
(signifying no change), or an array of replacement tokens, which MUST be valid
Marked AST nodes. The replacement tokens are spliced into the array, replacing
the original token.

Note that unlike `marked.walkTokens`, this performs a shallow traversal.
*/
export function mdTokensEdit(tokens, fun, ...args) {
  uc.arrEdit(tokens, fun, ...args)
}

/*
Tool for editing Marked AST.

The input must be a single Marked "token" (AST node) generated by a Marked
Lexer. The function is called for each immediate child of the token (but not
the token itself!), and may return an array of tokens that should replace it.

Adapted from `marked.walkTokens`. Note that unlike `marked.walkTokens`, this
performs a shallow traversal.
*/
export function mdTokenEdit(token, fun, ...args) {
  a.reqStruct(token)
  const {type} = token

  if (type === `table`) {
    for (const val of a.values(token.header)) {
      mdTokensEdit(val.tokens, fun, ...args)
    }
    for (const cell of a.values(token.cells)) {
      for (const val of a.values(cell)) {
        mdTokensEdit(val.tokens, fun, ...args)
      }
    }
    return
  }

  if (type === `list`) {
    mdTokensEdit(token.items, fun, ...args)
    return
  }

  mdTokensEdit(token.tokens, fun, ...args)
}

function mdTokenText(text) {
  text = a.laxStr(text)
  return {type: `text`, raw: text, text}
}

export function articleToHtml(article) {
  const {content, articleLinks, allowLinks} = a.laxStruct(article)
  const opt = new MdOpt()
  const tokens = opt.lexer.lex(a.laxStr(content))

  new ArticleEditor(tokens, articleLinks, allowLinks).run()

  return opt.parser.parse(tokens)
}

function articleLinksReg(links /* : []AnchorParams */) {
  if (a.isEmpty(links)) return undefined
  const pattern = a.compact(a.map(links, articleLinkPattern)).join(`|`)
  return RegExp(pattern, `gi`)
}

function articleLinkPattern({text} /* : AnchorParams */) {
  a.reqValidStr(text)
  return `\\b(${a.regEsc(text)}[\\w-]*)\\b`
}

/*
Class is used to save information about links that have already been used to exclude their future occurrences.
*/
class ArticleEditor extends a.Emp {
  constructor(tokens, links, allowLinks) {
    super()
    um.bindAll(this)

    this.tokens = tokens
    this.links = a.arr(links)
    this.allowLinks = allowLinks
    this.reg = articleLinksReg(this.links)
  }

  run() {
    // See comments on `articleMdEdit` for explanations.
    mdTokensEdit(this.tokens, this.edit)
  }

  /*
  Modifies the Marked AST generated from article content, according to our
  business logic.

    * May or may not turn original links into plain text.

    * May wrap matching text into additional links, based on `.articleLinks`
      provided by the server as part of the article.
  */
  edit(token, preventMatchLink) {
    const {reg} = this

    if (token.type === `link`) {
      mdTokenEdit(token, this.editLink)
      if (ut.isSiteUrl(token.href) || this.allowLinks) return undefined
      return token.tokens
    }

    if (token.type === `text` && !token.tokens && !preventMatchLink) {
      const text = a.laxStr(token.text)

      if (reg) {
        ut.regRes(reg)

        if (reg.test(text)) {
          ut.regRes(reg)
          return a.mapCompact(text.split(reg), this.toLinkMatch)
        }
      }

      return undefined
    }

    preventMatchLink ||= token.type === `heading`
    mdTokenEdit(token, this.edit, preventMatchLink)
    return undefined
  }

  /*
  Has side-effect removes used link after first match.

  TODO fix issue: the matched phrases of the first paragraph are not converted to
  links (for inst. `quantum physics`). Example:

    https://corespirit.com/articles/discovering-thought-field-therapy-tft-adf0c01qa
  */
  toLinkMatch(text) {
    const {reg, links} = this
    if (!text || !reg) return undefined

    ut.regRes(reg)
    const match = reg.exec(text)
    if (!match) return mdTokenText(text)

    const ind = um.matchIndex(match)
    const link = a.arr(links)[ind]
    if (!link) return mdTokenText(text)

    this.removeLink(ind)
    text = match[0]

    return {
      type: `link`,
      href: ut.url(link.url).setQuery(link.query).href,
      text,
      tokens: [mdTokenText(text)],
    }
  }

  // Variant of `articleMdEdit` that should be used on DESCENDANTS of links.
  editLink(token) {
    mdTokenEdit(token, this.editLink)
    if (token.type === `link`) return token.tokens
    return undefined
  }

  removeLink(ind) {
    this.links?.splice(ind, 1)
    this.reg = articleLinksReg(this.links)
  }
}

export function mdImgUploadPlaceholder(name) {
  return `<!-- uploading ${a.laxStr(name) || a.uuid()} -->`
}

export function mdImg(alt, src) {
  alt = a.laxStr(alt)
  src = a.renderLax(src)
  return src && (`![` + alt + `](` + src + `)`)
}
